"use client";

import React, { useEffect } from "react";
import ExportedImage from "next-image-export-optimizer";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ShieldCheck, BadgeCheck, RefreshCcw, Headset, Quote } from "lucide-react";

/** Market-test: REAL-proof module placement (market-test-plan.md §3
 *  `proof_real_reviews_placement_v1`; §10 required events review_module_*). */
const PROOF_EXPERIMENT_ID = "proof_real_reviews_placement_v1";

const onReviewInteract = (name: string) => {
  try {
    const eventId = `review_module_clicked:${PROOF_EXPERIMENT_ID}:${name}:${Date.now()}`;
    track({
      event_name: "review_module_clicked",
      event_id: eventId,
      experiment_id: PROOF_EXPERIMENT_ID,
    });
  } catch {
    /* analytics is best-effort */
  }
};

/**
 * Travel Connected — social-proof section. The mockups lean heavily on
 * unverifiable proof (4.8/5, "Trustpilot", "25,000+ reviews", "millions of
 * happy users"). NONE are reproduced. We keep the STRUCTURE (review cards +
 * trust badges) but with neutral, supportable copy only — no ratings, no
 * counts, no AggregateRating schema. Uses the 4 real local avatars.
 */
export default function SocialProof() {
  const { t } = useTranslations();

  // Proof-exposed-session signal (the brand's headline trust-thesis metric,
  // market-test-plan.md §10). Best-effort + consent-gated; `track` dedupes.
  useEffect(() => {
    try {
      track({
        event_name: "review_module_viewed",
        event_id: `review_module_viewed:${PROOF_EXPERIMENT_ID}`,
        experiment_id: PROOF_EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const reviews = [
    { img: "/images/testimonials/emily-chen.jpg", name: t("pc.proof.r1.name", "Emily C."), trip: t("pc.proof.r1.trip", "Japan"), quote: t("pc.proof.r1.quote", "Scanned the QR code at the airport and I was online before I left baggage claim.") },
    { img: "/images/testimonials/john-martinez.jpg", name: t("pc.proof.r2.name", "John M."), trip: t("pc.proof.r2.trip", "United Kingdom"), quote: t("pc.proof.r2.quote", "Bought it the night before my flight. Setup took two minutes and just worked.") },
    { img: "/images/testimonials/sarah-johnson.jpg", name: t("pc.proof.r3.name", "Sarah J."), trip: t("pc.proof.r3.trip", "Thailand"), quote: t("pc.proof.r3.quote", "Kept my home number for messages and used the eSIM for data. No roaming bill.") },
    { img: "/images/testimonials/carlos-rodriguez.jpg", name: t("pc.proof.r4.name", "Carlos R."), trip: t("pc.proof.r4.trip", "USA"), quote: t("pc.proof.r4.quote", "Clear pricing, instant delivery, and support actually replied fast. Recommended.") },
  ];

  const badges = [
    { icon: ShieldCheck, label: t("pc.proof.b1", "Secure checkout") },
    { icon: RefreshCcw, label: t("pc.proof.b2", "Money-back guarantee") },
    { icon: BadgeCheck, label: t("pc.proof.b3", "No contract") },
    { icon: Headset, label: t("pc.proof.b4", "24/7 support") },
  ];

  return (
    <section className="container mx-auto px-4 py-12">
      <Reveal as="div" className="text-center max-w-2xl mx-auto mb-8" y={18}>
        <h2 className="text-2xl sm:text-3xl text-foreground">{t("pc.proof.title", "Loved by travelers")}</h2>
        <p className="mt-2 text-muted-foreground">{t("pc.proof.sub", "Real travelers staying connected on the trips that matter.")}</p>
      </Reveal>

      <Stagger as="div" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" staggerChildren={0.08} amount={0.2}>
        {reviews.map((r) => (
          <StaggerItem as="figure" key={r.name} y={18} className="pc-card p-5 flex flex-col cursor-default transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
            <span onClick={() => onReviewInteract(r.name)} className="contents">
              <Quote className="w-6 h-6 text-primary/30 mb-2" aria-hidden="true" />
              <blockquote className="text-sm text-foreground/90 flex-1">{r.quote}</blockquote>
              <figcaption className="mt-4 flex items-center gap-3">
                <span className="relative w-10 h-10 rounded-full overflow-hidden ring-1 ring-border shrink-0">
                  <ExportedImage src={r.img} alt={r.name} fill sizes="40px" className="object-cover" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground">{r.name}</span>
                  <span className="block text-xs text-muted-foreground">{r.trip}</span>
                </span>
              </figcaption>
            </span>
          </StaggerItem>
        ))}
      </Stagger>

      {/* Trust badges (neutral, supportable) */}
      <Reveal as="div" className="mt-6 pc-card p-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3" y={16}>
        {badges.map(({ icon: Icon, label }) => (
          <span key={label} className="inline-flex items-center gap-2 text-sm font-semibold text-foreground/80">
            <Icon className="w-5 h-5 text-primary" />{label}
          </span>
        ))}
      </Reveal>
    </section>
  );
}
